// Statements engine: a statement project and the policy requirements for its site in, figures and a compliance table
// out. Pure — runs in the browser and on the server. House types carry SAP figures (from the hub's SAP plots, a library
// match, or a benchmark when there is no SAP yet); site figures are floor-area weighted (rate × TFA × count). See
// docs/STATEMENTS.md for the method and statements.ts for what each PolicyMetric means.
import type {
  AppliesTo, ComplianceRow, HouseTypeResult, PartLEdition, PolicyPlan, PolicyRequirement, SapFigures, SiteResults,
  StatementHouseType, StatementProject, StatementResults,
} from './statements'
import {
  ADMD_ALL_ELECTRIC_KVA, BENCHMARK_TOLERANCE, ELEC_CO2, GAS_CO2, L2021_BELOW_L2013, L2021_NOTIONAL_BOILER_EFF,
  L2026_NOTIONAL_HP_EFF, PV_CREDIT, PV_M2_PER_KWP, PV_YIELD_KWH_PER_KWP, benchmarkSap, groundFloorAreaOf, l3MinimumKwp,
} from './statementsBenchmarks'
import { draftAppraisal } from './statementsLzc'
import { PLAN_STATUS_LABEL, describeRequirement, fmt, fmtInt, isEmerging } from './statementsText'

export * from './statementsBenchmarks'
export * from './statementsLzc'
export * from './statementsText'

/**
 * Unregulated energy (appliances and cooking) added to SAP's regulated energy for EUI and the renewable match,
 * kWh/m²/yr. SAP 10.2 Appendix L gives about 23 kWh/m²/yr of appliances for a 94 m² home; the Cornwall and B&NES SAP
 * Conversion Tool (methodology Rev C, Sept 2023, s.4) replaces that with a bottom-up figure for modern appliances of
 * about half (≈ 11) and keeps SAP's cooking (≈ 5), which gives the 16 used here.
 */
export const UNREGULATED_KWH_M2 = 16
export const UNREGULATED_SOURCE = 'after the Cornwall Council and B&NES SAP Conversion Tool methodology, Rev C, September 2023: modern appliances at about half the SAP 10.2 Appendix L figure, plus SAP cooking'

/** Major development: 10+ dwellings, a site of 0.5 ha or more, or 1,000 m² of non-residential floorspace. */
const MAJOR_DWELLINGS = 10
const MAJOR_SITE_HA = 0.5
const MAJOR_FLOORSPACE_M2 = 1000
/** Referable to the Mayor of London (Category 1A): 150 or more residential units. */
const REFERABLE_DWELLINGS = 150

export interface EvaluateExtras {
  /** From the site's Grid project. */
  gridHeadroomKva?: number | null
  /** The scheme's design demand from its Grid project (ADMD, kVA); without it, dwellings × ADMD_ALL_ELECTRIC_KVA. */
  gridDemandKva?: number | null
  /** From the site's WLC project, kgCO2e/m² GIA (A1–A5). */
  wlcUpfrontKgM2?: number | null
  /** Roof area suitable for PV on one dwelling of each house type, from the models: caps L3 at what is practicable. */
  roofs?: { houseTypeId: string; pvAreaM2: number }[] | null
  heatNetworkNearby?: boolean
  aqma?: boolean
  conservationArea?: boolean
  /** Date the rules are read at (ISO yyyy-mm-dd); today when absent. */
  asOf?: string
}

// ---------------------------------------------------------------- the site

export interface SiteFacts {
  dwellings: number
  totalFloorArea: number
  nonResidentialM2: number
  major: boolean
  referable: boolean
  flats: boolean
  forms: Set<string>
}

const formKey = (ht: StatementHouseType) => (ht.propertyType === 'flat' || ht.propertyType === 'maisonette' ? 'flat' : ht.builtForm ?? ht.propertyType)
const normForm = (f: string) => {
  const k = f.trim().toLowerCase()
  return k === 'semi' ? 'semi-detached' : k === 'flats' || k === 'apartment' || k === 'apartments' ? 'flat' : k
}

export function siteFacts(project: StatementProject): SiteFacts {
  const types = project.houseTypes.filter((h) => h.count > 0)
  const dwellings = types.reduce((a, h) => a + h.count, 0)
  const totalFloorArea = types.reduce((a, h) => a + h.tfa * h.count, 0)
  return {
    dwellings,
    totalFloorArea,
    nonResidentialM2: project.nonResidentialM2 || 0,
    major: dwellings >= MAJOR_DWELLINGS || (project.siteAreaHa ?? 0) >= MAJOR_SITE_HA || (project.nonResidentialM2 || 0) >= MAJOR_FLOORSPACE_M2,
    referable: dwellings >= REFERABLE_DWELLINGS,
    flats: types.some((h) => formKey(h) === 'flat'),
    forms: new Set(types.map(formKey)),
  }
}

/** Null when the rule applies to the site; otherwise why it does not. */
export function whyNotApplies(a: AppliesTo, f: SiteFacts): string | null {
  if (a.minDwellings != null && f.dwellings < a.minDwellings) return `Applies to ${a.minDwellings}+ dwellings; this site has ${f.dwellings}`
  if (a.maxDwellings != null && f.dwellings > a.maxDwellings) return `Applies up to ${a.maxDwellings} dwellings; this site has ${f.dwellings}`
  if (a.minFloorspaceM2 != null && f.totalFloorArea + f.nonResidentialM2 < a.minFloorspaceM2) return `Applies from ${fmtInt(a.minFloorspaceM2)} m² of floorspace`
  if (a.major && !f.major) return 'Applies to major development (10+ dwellings or 0.5 ha+); this site is not major'
  if (a.referable && !f.referable) return 'Applies to schemes referable to the Mayor (150+ units)'
  if (a.use === 'residential' && f.dwellings === 0) return 'Residential only; the site has no dwellings'
  if (a.use === 'non-residential' && f.nonResidentialM2 <= 0) return 'Non-residential only; the site has no non-residential floorspace'
  if (a.builtForms?.length && !a.builtForms.some((b) => f.forms.has(normForm(b)))) return `No ${a.builtForms.join(' / ')} dwellings on this site`
  return null
}

// ---------------------------------------------------------------- house types

interface TypeCalc {
  ht: StatementHouseType
  sap: SapFigures
  estimated: boolean
  regulatedKwh: number
  regulatedEstimated: boolean
  spaceHeating: number | null
  shFromDfee: boolean
  eui: number
  pvKwp: number
  pvKwh: number
  pvKwhEstimated: boolean
  l3Kwp: number
  l3RoofCapped: boolean
  gfaEstimated: boolean
  /** kgCO2/yr per dwelling after each stage of the hierarchy. */
  base: number
  lean: number
  clean: number
  green: number
  fabricKnown: boolean
}

const ELECTRIC_HEATING = /heat pump|ashp|gshp|electric|storage heater/i

function typeCalc(ht: StatementHouseType, project: StatementProject, roofs: EvaluateExtras['roofs']): TypeCalc {
  const s = project.strategy
  const estimated = ht.source === 'benchmark' || !ht.sap
  const sap = estimated ? benchmarkSap(ht, project.partL, s.pvKwpPerDwelling) : ht.sap!
  const tfa = Math.max(1, ht.tfa)

  const pvKwp = sap.pvKwp ?? s.pvKwpPerDwelling ?? 0
  const pvKwhEstimated = sap.pvKwh == null && pvKwp > 0
  const pvKwh = sap.pvKwh ?? pvKwp * PV_YIELD_KWH_PER_KWP

  // Regulated energy from SAP; when only the rates are known, back it out of the DER (PV credit added back) at the
  // factor of the heating fuel — rough, and flagged.
  let regulatedKwh = sap.regulatedKwh ?? 0
  const regulatedEstimated = sap.regulatedKwh == null
  if (regulatedEstimated) {
    const electric = ELECTRIC_HEATING.test(sap.heating ?? '') || ['ashp', 'gshp', 'direct-electric'].includes(s.heating)
    const pvCredit = pvKwh * ELEC_CO2 * PV_CREDIT
    regulatedKwh = Math.max(0, (sap.der * tfa + pvCredit) / (electric ? ELEC_CO2 : GAS_CO2))
  }

  const shFromDfee = sap.spaceHeating == null && sap.dfee != null
  const spaceHeating = sap.spaceHeating ?? sap.dfee ?? null
  const eui = regulatedKwh / tfa + UNREGULATED_KWH_M2

  const gfa = groundFloorAreaOf(ht)
  let l3Kwp = l3MinimumKwp(gfa.area)
  const roof = roofs?.find((r) => r.houseTypeId === ht.id)
  const l3RoofCapped = !!roof && roof.pvAreaM2 / PV_M2_PER_KWP < l3Kwp
  if (l3RoofCapped) l3Kwp = roof!.pvAreaM2 / PV_M2_PER_KWP

  // Hierarchy, kgCO2/yr per dwelling. Be Lean: the TER less the fabric saving, i.e. the drop from TFEE to DFEE met by
  // the notional heating (L2021 gas boiler 89.5% at 0.210; L2026 heat pump 250% at 0.136).
  const base = sap.ter * tfa
  const prop = sap.der * tfa
  const fabricKnown = sap.tfee != null && sap.dfee != null
  const fabricSaving = fabricKnown
    ? (sap.tfee! - sap.dfee!) * tfa * (project.partL === 'L2021' ? GAS_CO2 / L2021_NOTIONAL_BOILER_EFF : ELEC_CO2 / L2026_NOTIONAL_HP_EFF)
    : 0
  const lean = base - fabricSaving
  // Be Clean: a heat network takes everything but the PV; otherwise nothing. Be Green: heat pump + PV down to the DER.
  const pvSaving = pvKwh * ELEC_CO2 * PV_CREDIT
  const clean = s.heating === 'heat-network' ? Math.max(prop, Math.min(lean, prop + pvSaving)) : lean

  return {
    ht, sap, estimated, regulatedKwh, regulatedEstimated, spaceHeating, shFromDfee, eui, pvKwp, pvKwh, pvKwhEstimated,
    l3Kwp, l3RoofCapped, gfaEstimated: gfa.estimated, base, lean, clean, green: prop, fabricKnown,
  }
}

const weighted = (cs: TypeCalc[], pick: (c: TypeCalc) => number | null): number | null => {
  let num = 0, den = 0
  for (const c of cs) {
    const v = pick(c)
    if (v == null) continue
    const w = c.ht.tfa * c.ht.count
    num += v * w
    den += w
  }
  return den > 0 ? num / den : null
}
const sumT = (cs: TypeCalc[], pick: (c: TypeCalc) => number) => cs.reduce((a, c) => a + pick(c) * c.ht.count, 0) / 1000

// ---------------------------------------------------------------- compliance

type Edition = PartLEdition | 'L2013'
const editionOf = (baseline: string | null, fallback: PartLEdition): Edition => {
  if (!baseline) return fallback
  if (/2013/.test(baseline)) return 'L2013'
  if (/2026|future homes/i.test(baseline)) return 'L2026'
  if (/2021/.test(baseline)) return 'L2021'
  return fallback
}
const EDITION_LABEL: Record<Edition, string> = { L2013: 'Part L 2013', L2021: 'Part L 2021', L2026: 'Part L 2026' }

interface MetricResult { achieved: string; outcome: ComplianceRow['outcome']; note: string | null }

const passIf = (ok: boolean): ComplianceRow['outcome'] => (ok ? 'pass' : 'fail')
const EPS = 1e-9
const pct1 = (n: number) => `${fmt(n, 1)}%`

function metricResult(r: PolicyRequirement, project: StatementProject, calcs: TypeCalc[], site: SiteResults, f: SiteFacts, x: EvaluateExtras): MetricResult {
  const v = r.value
  const s = project.strategy
  const forms = r.appliesTo.builtForms?.map(normForm)
  const subset = forms?.length ? calcs.filter((c) => forms.includes(formKey(c.ht))) : calcs
  const scope = forms?.length ? `, ${forms.join(', ')}` : ''
  const noValue: MetricResult = { achieved: '—', outcome: 'info', note: 'The requirement has no figure to test against.' }

  switch (r.metric) {
    case 'ter_reduction_pct': {
      if (v == null) return noValue
      const ed = editionOf(r.baseline, project.partL)
      // SAP 10.2 DERs against a SAP 10.3 TER mix two sets of carbon factors (and the FHS TER is near zero): the
      // Part L 2026 test belongs to the plots assessed under it
      if (ed === 'L2026' && project.partL !== 'L2026')
        return { achieved: '—', outcome: 'info', note: 'Applies to plots assessed under Part L 2026 (SAP 10.3); this statement’s figures are Part L 2021 (SAP 10.2).' }
      const terIn = (c: TypeCalc): number => {
        if (ed === project.partL) return c.sap.ter
        if (ed === 'L2013') return (project.partL === 'L2021' ? c.sap.ter : benchmarkSap(c.ht, 'L2021').ter) / (1 - L2021_BELOW_L2013)
        return benchmarkSap(c.ht, ed).ter
      }
      const note = ed === project.partL ? null : ed === 'L2013'
        ? `Measured against Part L 2013: the ${project.partL === 'L2021' ? 'Part L 2021 TER' : 'estimated Part L 2021 TER'} taken as ${fmtInt(L2021_BELOW_L2013 * 100)}% below the 2013 TER.`
        : `Measured against ${EDITION_LABEL[ed]}, whose TER is estimated from benchmark figures for each house type.`
      const baseT = subset.reduce((a, c) => a + terIn(c) * c.ht.tfa * c.ht.count, 0)
      const propT = subset.reduce((a, c) => a + c.sap.der * c.ht.tfa * c.ht.count, 0)
      // under 1 kgCO2/m²/yr on average a percentage of it says nothing (the FHS notional dwelling's PV covers most of it)
      const area = subset.reduce((a, c) => a + c.ht.tfa * c.ht.count, 0)
      if (baseT <= 0 || baseT < area) return { achieved: '—', outcome: 'info', note: 'The baseline TER is at or near zero, so a percentage reduction is not meaningful.' }
      const red = ((baseT - propT) / baseT) * 100
      return { achieved: `${pct1(red)} below the ${EDITION_LABEL[ed]} TER (area-weighted${scope})`, outcome: passIf(red >= v - EPS), note }
    }
    case 'onsite_reduction_pct':
      if (v == null) return noValue
      if (site.baselineCo2 <= 0) return { achieved: '—', outcome: 'info', note: 'The baseline TER is at or below zero, so a percentage reduction is not meaningful.' }
      return { achieved: `${pct1(site.reductionPct)} on-site reduction (${fmt(site.baselineCo2, 2)} to ${fmt(site.proposedCo2, 2)} tCO2/yr)`, outcome: passIf(site.reductionPct >= v - EPS), note: null }
    case 'be_lean_pct': {
      if (v == null) return noValue
      const lean = site.hierarchy.find((h) => h.stage === 'be-lean')!
      return {
        achieved: `${pct1(lean.savedPct)} from energy efficiency (Be Lean)`,
        outcome: passIf(lean.savedPct >= v - EPS),
        note: calcs.every((c) => c.fabricKnown) ? null : 'Some house types have no TFEE/DFEE, so their fabric saving is taken as nil.',
      }
    }
    case 'renewables_pct_energy':
      if (v == null) return noValue
      if (site.energyUseKwh <= 0) return { achieved: '—', outcome: 'info', note: 'No energy use figure.' }
      return {
        achieved: `${pct1(site.renewableSharePct)} of energy use from on-site renewables (${fmtInt(site.renewableKwh)} of ${fmtInt(site.energyUseKwh)} kWh/yr)`,
        outcome: passIf(site.renewableSharePct >= v - EPS),
        note: 'Counts PV generation; the ambient heat captured by heat pumps is not counted.',
      }
    case 'renewables_pct_co2': {
      if (v == null) return noValue
      const clean = site.hierarchy.find((h) => h.stage === 'be-clean')!.co2
      const red = clean > 0 ? ((clean - site.proposedCo2) / clean) * 100 : 0
      return { achieved: `${pct1(red)} CO2 reduction from on-site renewables (heat pump and PV, against the emissions before them)`, outcome: passIf(red >= v - EPS), note: null }
    }
    case 'renewable_match': {
      if (site.energyUseKwh <= 0) return { achieved: '—', outcome: 'info', note: 'No energy use figure.' }
      const share = (site.renewableKwh / site.energyUseKwh) * 100
      return { achieved: `${fmtInt(site.renewableKwh)} kWh/yr generated against ${fmtInt(site.energyUseKwh)} kWh/yr used (${fmtInt(share)}%)`, outcome: passIf(site.renewableKwh >= site.energyUseKwh - EPS), note: null }
    }
    case 'shd_kwh_m2':
    case 'eui_kwh_m2':
    case 'eui_max_dwelling_kwh_m2': {
      if (v == null) return noValue
      const isShd = r.metric === 'shd_kwh_m2'
      const pick = isShd ? (c: TypeCalc) => c.spaceHeating : (c: TypeCalc) => c.eui
      const what = isShd ? 'space heating demand' : 'EUI'
      if (r.metric === 'eui_max_dwelling_kwh_m2' || r.appliesTo.basis === 'per-dwelling') {
        const known = subset.filter((c) => pick(c) != null)
        if (!known.length) return { achieved: '—', outcome: 'info', note: `No ${what} figure for these house types.` }
        const worst = known.reduce((a, c) => (pick(c)! > pick(a)! ? c : a))
        const got = pick(worst)!
        return { achieved: `${fmt(got, 1)} kWh/m²/yr ${what}, highest dwelling (${worst.ht.name})`, outcome: passIf(got <= v + EPS), note: isShd && known.some((c) => c.shFromDfee) ? 'DFEE used where SAP gave no space heating demand.' : null }
      }
      const got = weighted(subset, pick)
      if (got == null) return { achieved: '—', outcome: 'info', note: `No ${what} figure for these house types.` }
      return {
        achieved: `${fmt(got, 1)} kWh/m²/yr ${what} (site average${scope})`,
        outcome: passIf(got <= v + EPS),
        note: isShd && subset.some((c) => c.shFromDfee) ? 'DFEE used where SAP gave no space heating demand.' : null,
      }
    }
    case 'zero_carbon_offset': {
      if (v == null) return noValue
      const years = r.value2 ?? 30
      const t = Math.max(0, site.proposedCo2)
      return { achieved: `${fmt(t, 2)} tCO2/yr × ${years} years × £${fmtInt(v)}/t = £${fmtInt(t * years * v)}`, outcome: 'info', note: 'The remaining regulated CO2 is offset by payment.' }
    }
    case 'water_lpd':
      if (v == null) return noValue
      if (s.waterLpd == null) return { achieved: 'Not yet specified', outcome: 'info', note: 'Set the design water use in the strategy.' }
      return { achieved: `${fmtInt(s.waterLpd)} litres/person/day`, outcome: passIf(s.waterLpd <= v + EPS), note: null }
    case 'ev_chargers':
      if (v == null) return noValue
      if (s.evChargersPerDwelling == null) return { achieved: 'Not yet specified', outcome: 'info', note: 'Set the charge points per dwelling in the strategy.' }
      return { achieved: `${fmt(s.evChargersPerDwelling, s.evChargersPerDwelling % 1 ? 1 : 0)} per dwelling`, outcome: passIf(s.evChargersPerDwelling >= v - EPS), note: null }
    case 'wlc_kgco2e_m2':
      if (v == null) return noValue
      if (x.wlcUpfrontKgM2 == null) return { achieved: 'No whole life carbon assessment linked', outcome: 'info', note: null }
      // embodied carbon comes from the separate whole life carbon assessment; it is never added to the operational figures
      return { achieved: `${fmtInt(x.wlcUpfrontKgM2)} kgCO2e/m² upfront (A1–A5)`, outcome: passIf(x.wlcUpfrontKgM2 <= v + EPS), note: 'From the whole life carbon assessment, reported separately.' }
    case 'heat_network':
      if (s.heating === 'heat-network') return { achieved: 'Heat network connection proposed', outcome: 'pass', note: null }
      if (x.heatNetworkNearby) return { achieved: 'A heat network is nearby but not proposed', outcome: 'fail', note: 'Set out why connection is not taken up, and keep the dwellings ready to connect.' }
      return x.heatNetworkNearby === false
        ? { achieved: 'No heat network within reach of the site', outcome: 'pass', note: null }
        : { achieved: 'Heat network availability not yet checked', outcome: 'info', note: null }
    case 'monitoring':
      return s.monitoring
        ? { achieved: `Monitoring committed${v ? ` for ${fmtInt(v)} years` : ''}`, outcome: 'pass', note: null }
        : { achieved: 'Not yet committed', outcome: 'fail', note: null }
    case 'breeam':
      return { achieved: r.text ?? 'BREEAM', outcome: 'info', note: f.nonResidentialM2 > 0 ? `Applies to the ${fmtInt(f.nonResidentialM2)} m² of non-residential floorspace.` : 'The site has no non-residential floorspace.' }
    case 'tm59':
      return { achieved: 'Dynamic overheating assessment to CIBSE TM59', outcome: 'info', note: r.text }
    case 'statement':
    case 'other':
      return { achieved: 'Addressed in this statement', outcome: 'info', note: null }
  }
}

function complianceRow(r: PolicyRequirement, plan: PolicyPlan | undefined, project: StatementProject, calcs: TypeCalc[], site: SiteResults, f: SiteFacts, x: EvaluateExtras, asOf: string): ComplianceRow {
  const row = (achieved: string, outcome: ComplianceRow['outcome'], note: string | null): ComplianceRow => ({
    requirementId: r.id, planName: plan?.name ?? '', policyRef: r.policyRef, topic: r.topic,
    requirement: describeRequirement(r), achieved, outcome, note,
  })
  const excluded = project.excluded.find((e) => e.id === r.id)
  if (excluded) return row('—', 'n-a', `Excluded for this site: ${excluded.reason}`)
  if (plan?.status === 'superseded') return row('—', 'n-a', 'The plan has been superseded.')
  if (r.effectiveTo && r.effectiveTo < asOf) return row('—', 'n-a', `Ceased to apply on ${r.effectiveTo}.`)
  const why = whyNotApplies(r.appliesTo, f)
  if (why) return row('—', 'n-a', why)

  const m = metricResult(r, project, calcs, site, f, x)
  const notes = [m.note]
  let outcome = m.outcome
  const decided = outcome === 'pass' || outcome === 'fail'
  const would = decided ? ` The scheme would ${outcome === 'pass' ? '' : 'not '}meet it.` : ''
  if (plan && isEmerging(plan.status)) {
    notes.unshift(`Emerging plan (${PLAN_STATUS_LABEL[plan.status]}): shown for information, not yet part of the development plan.${would}`)
    outcome = 'info'
  } else if (r.effectiveFrom && r.effectiveFrom > asOf) {
    notes.unshift(`Applies from ${r.effectiveFrom}.${would}`)
    outcome = 'info'
  } else if (r.status === 'review') {
    notes.unshift(`Awaiting verification against the plan text.${would}`)
    outcome = 'info'
  }
  if (r.weightNote) notes.push(r.weightNote)
  const note = notes.filter(Boolean).join(' ')
  return row(m.achieved, outcome, note || null)
}

// ---------------------------------------------------------------- evaluate

/** Figures, compliance and the technology appraisal for a statement. Rejected requirements are left out. */
export function evaluate(project: StatementProject, requirements: PolicyRequirement[], plans: PolicyPlan[], extras: EvaluateExtras = {}): StatementResults {
  const warnings: string[] = []
  const basis: string[] = []
  const asOf = extras.asOf ?? new Date().toISOString().slice(0, 10)
  const f = siteFacts(project)

  const empty = project.houseTypes.filter((h) => !(h.count > 0) || !(h.tfa > 0))
  if (empty.length) warnings.push(`Left out (no dwellings or no floor area): ${empty.map((h) => h.name).join(', ')}.`)
  const calcs = project.houseTypes.filter((h) => h.count > 0 && h.tfa > 0).map((h) => typeCalc(h, project, extras.roofs))
  if (!calcs.length) warnings.push('No house types with dwellings and floor area yet.')

  const houseTypes: HouseTypeResult[] = calcs.map((c) => ({
    id: c.ht.id, name: c.ht.name, count: c.ht.count, tfa: c.ht.tfa,
    source: c.estimated ? 'benchmark' : c.ht.source,
    ter: c.sap.ter, der: c.sap.der,
    improvement: c.sap.ter !== 0 ? ((c.sap.ter - c.sap.der) / c.sap.ter) * 100 : 0,
    dfee: c.sap.dfee, tfee: c.sap.tfee, dper: c.sap.dper, tper: c.sap.tper,
    spaceHeating: c.spaceHeating, eui: c.eui, pvKwp: c.pvKwp, pvKwh: c.pvKwh,
  }))

  const baselineCo2 = sumT(calcs, (c) => c.base)
  const leanCo2 = sumT(calcs, (c) => c.lean)
  const cleanCo2 = sumT(calcs, (c) => c.clean)
  const proposedCo2 = sumT(calcs, (c) => c.green)
  const saved = (a: number, b: number) => (baselineCo2 !== 0 ? ((a - b) / baselineCo2) * 100 : 0)
  const energyUseKwh = calcs.reduce((a, c) => a + (c.regulatedKwh + UNREGULATED_KWH_M2 * c.ht.tfa) * c.ht.count, 0)
  const renewableKwh = calcs.reduce((a, c) => a + c.pvKwh * c.ht.count, 0)
  const pvKwpTotal = calcs.reduce((a, c) => a + c.pvKwp * c.ht.count, 0)
  const pvKwpL3 = calcs.length ? calcs.reduce((a, c) => a + c.l3Kwp * c.ht.count, 0) : null

  const site: SiteResults = {
    dwellings: f.dwellings,
    totalFloorArea: f.totalFloorArea,
    baselineCo2,
    proposedCo2,
    reductionPct: saved(baselineCo2, proposedCo2),
    hierarchy: [
      { stage: 'baseline', co2: baselineCo2, savedPct: 0 },
      { stage: 'be-lean', co2: leanCo2, savedPct: saved(baselineCo2, leanCo2) },
      { stage: 'be-clean', co2: cleanCo2, savedPct: saved(leanCo2, cleanCo2) },
      { stage: 'be-green', co2: proposedCo2, savedPct: saved(cleanCo2, proposedCo2) },
    ],
    spaceHeating: weighted(calcs, (c) => c.spaceHeating),
    eui: weighted(calcs, (c) => c.eui),
    energyUseKwh,
    renewableKwh,
    renewableSharePct: energyUseKwh > 0 ? (renewableKwh / energyUseKwh) * 100 : 0,
    pvKwpTotal,
    pvKwpL3,
    offset: null,
    basis,
  }

  // ---- basis: what was measured and what was estimated
  const measured = calcs.filter((c) => !c.estimated)
  const est = calcs.filter((c) => c.estimated)
  if (measured.length) {
    const lib = measured.filter((c) => c.ht.source === 'library').map((c) => c.ht.name)
    basis.push(`${measured.length} of ${calcs.length} house types from ${project.partL === 'L2026' ? 'SAP 10.3' : 'SAP 10.2'} calculations${lib.length ? ` (${lib.join(', ')} from the same house type on another site)` : ''}.`)
  }
  if (est.length) {
    const tol = fmtInt(BENCHMARK_TOLERANCE[project.partL] * 100)
    const names = est.map((c) => c.ht.name).join(', ')
    basis.push(`${names}: benchmark estimates for the built form and floor area under ${EDITION_LABEL[project.partL]}, accurate to about ±${tol}%, assuming an air source heat pump, PV and fabric at the notional specification (SAP 10.2 carbon factors). Design-stage SAP calculations will replace them before commencement.`)
    warnings.push(`${est.length} house type${est.length > 1 ? 's use' : ' uses'} benchmark estimates (±${tol}%), not SAP calculations: ${names}.`)
    const missingSap = est.filter((c) => c.ht.source !== 'benchmark')
    if (missingSap.length) warnings.push(`No SAP figures for ${missingSap.map((c) => c.ht.name).join(', ')}, so benchmark estimates are used.`)
    if (project.strategy.heating !== 'ashp') warnings.push('Benchmark figures assume an air source heat pump; the strategy uses different heating.')
  }
  basis.push('Site figures are floor-area weighted: each rate × total floor area × number of dwellings; carbon in tCO2/yr.')
  basis.push(project.partL === 'L2021'
    ? 'Baseline: the Part L 2021 Target Emission Rate. Be Lean is estimated as the TER less the fabric saving, i.e. the drop from TFEE to DFEE met by the notional gas boiler (89.5%, 0.210 kgCO2/kWh); it covers fabric only, not ventilation or hot water measures.'
    : 'Baseline: the Part L 2026 Target Emission Rate. Be Lean is estimated as the TER less the fabric saving, i.e. the drop from TFEE to DFEE met by the notional heat pump (250%, 0.136 kgCO2/kWh); it covers fabric only, not ventilation or hot water measures.')
  if (calcs.some((c) => !c.fabricKnown)) basis.push('Where a house type has no TFEE/DFEE its fabric saving is taken as nil.')
  basis.push(project.strategy.heating === 'heat-network'
    ? 'Be Clean: the heat network connection (everything between Be Lean and the DER other than the PV). Be Green: the PV, credited at 85% of the SAP 10.2 electricity factor.'
    : 'Be Clean: no heat network, so no saving. Be Green: the heat pump and PV, from Be Lean down to the Dwelling Emission Rate.')
  basis.push(`Energy use intensity: SAP regulated energy plus an unregulated allowance of ${UNREGULATED_KWH_M2} kWh/m²/yr for appliances and cooking (${UNREGULATED_SOURCE}), over the total floor area. Councils' own conversion tools also adjust SAP's space heating and hot water, so their figures may differ.`)
  basis.push('Renewable share: PV generation over total energy use (regulated plus unregulated); the ambient heat captured by heat pumps is not counted.')
  if (calcs.some((c) => c.pvKwhEstimated && !c.estimated)) basis.push(`PV generation not given by SAP is taken as ${PV_YIELD_KWH_PER_KWP} kWh per kWp a year.`)
  const regEst = calcs.filter((c) => c.regulatedEstimated && !c.estimated)
  if (regEst.length) {
    basis.push('Regulated energy not given by SAP is backed out of the DER at the heating fuel\'s carbon factor.')
    warnings.push(`Regulated energy estimated from the DER for ${regEst.map((c) => c.ht.name).join(', ')}; EUI and the renewable match are approximate.`)
  }
  basis.push(`Part L 2026 L3 minimum PV: 0.22 kWp/m² over 40% of each dwelling's ground floor area${project.partL === 'L2021' ? ', shown as a forward look under Part L 2021' : ''}.`)
  if (calcs.some((c) => c.gfaEstimated)) basis.push('Ground floor area taken as the floor area over the storeys where it was not given (flats: their share of a three-storey block).')
  if (calcs.some((c) => c.l3RoofCapped)) basis.push('Where the suitable roof is smaller than the L3 figure, the L3 minimum is what the reasonably practicable roof area takes.')

  // ---- warnings on the figures
  if (project.partL === 'L2026' && pvKwpL3 != null && pvKwpTotal < pvKwpL3 - 0.05) {
    warnings.push(`PV of ${fmt(pvKwpTotal, 1)} kWp is below the Part L 2026 L3 minimum of ${fmt(pvKwpL3, 1)} kWp for the site.`)
  }
  for (const c of calcs) if (c.sap.der > c.sap.ter + EPS) warnings.push(`${c.ht.name}: DER ${fmt(c.sap.der, 2)} is above the TER ${fmt(c.sap.ter, 2)}.`)
  if (extras.gridHeadroomKva != null && ['ashp', 'gshp', 'direct-electric'].includes(project.strategy.heating)) {
    const need = extras.gridDemandKva ?? f.dwellings * ADMD_ALL_ELECTRIC_KVA
    if (extras.gridHeadroomKva < need) warnings.push(`Grid headroom of about ${fmtInt(extras.gridHeadroomKva)} kVA is below the roughly ${fmtInt(need)} kVA that ${f.dwellings} all-electric dwellings need after diversity.`)
  }

  // ---- compliance
  const planById = new Map(plans.map((p) => [p.id, p]))
  const live = requirements.filter((r) => r.status !== 'rejected')
  const compliance = live.map((r) => complianceRow(r, planById.get(r.planId), project, calcs, site, f, extras, asOf))
  const review = live.filter((r) => r.status === 'review')
  if (review.length) warnings.push(`${review.length} requirement${review.length > 1 ? 's are' : ' is'} awaiting verification and shown for information only.`)

  // The offset comes from the first in-force offset requirement that applies to the site.
  const offsetReq = live.find((r, i) => r.metric === 'zero_carbon_offset' && r.value != null && compliance[i].outcome !== 'n-a'
    && !isEmerging(planById.get(r.planId)?.status) && !(r.effectiveFrom && r.effectiveFrom > asOf) && r.status === 'live')
  if (offsetReq) {
    const years = offsetReq.value2 ?? 30
    const t = Math.max(0, proposedCo2)
    site.offset = { tonnesPerYear: t, years, pricePerTonne: offsetReq.value!, payment: t * years * offsetReq.value! }
    basis.push(`Offset: the remaining regulated CO2 (${fmt(t, 2)} t/yr) × ${years} years × £${fmtInt(offsetReq.value!)}/t.`)
  }

  const results: StatementResults = { houseTypes, site, compliance, lzc: [], warnings }
  const roofAreaM2 = extras.roofs?.length
    ? calcs.reduce((a, c) => a + (extras.roofs!.find((r) => r.houseTypeId === c.ht.id)?.pvAreaM2 ?? 0) * c.ht.count, 0)
    : null
  results.lzc = draftAppraisal(project, results, {
    flats: f.flats, aqma: extras.aqma, heatNetworkNearby: extras.heatNetworkNearby,
    gridHeadroomKva: extras.gridHeadroomKva, gridDemandKva: extras.gridDemandKva, gridFiguresInText: !!project.gridInReport, roofAreaM2, conservationArea: extras.conservationArea,
  })
  return results
}
